/**
 * @fileoverview Parcel data configuration form for the system-web
 *              DepartmentParcelDataTab. Shown when a department has no
 *              Regrid parcel data configuration, allowing the admin to
 *              configure it inline (the admin-side equivalent of onboarding
 *              Step 9 — Initial Data Setup).
 * @module system-web/components/ParcelDataConfigForm
 *
 * Responsibilities:
 * - Auto-match a Regrid cache file using the department's jurisdiction
 *   (county FIPS or state + county name).
 * - Let the admin override the auto-matched file by browsing all available
 *   cache files.
 * - Select the spatial filtering mode (all parcels, department boundary,
 *   fire hazard overlay) with the same UX as the onboarding wizard.
 * - Select hazard levels when fire hazard overlay mode is chosen.
 * - Choose whether the import materializes primary-owner contacts.
 * - Set the number of update tokens per year.
 * - Save the configuration via the useDepartmentParcelData hook.
 * - Trigger an admin-bypass import (no token consumed) after saving.
 *
 * Dependencies:
 * - ../hooks/useParcelCacheFiles (cache file auto-match + browse).
 * - @firesquirrel/core-domain (HAZARD_LEVEL_LABELS, FireHazardLevel).
 * - @ui-admin/contexts/ThemeContext.
 * - lucide-react icons.
 *
 * Architecture:
 * - Local-first. Reads cache files from Dexie via useParcelCacheFiles hook.
 * - Save and trigger actions go through the useDepartmentParcelData hook
 *   (write-through to system-api → Dexie put).
 * - No direct API calls from this component.
 *
 * Notes:
 * - The form is derived from the onboarding wizard's ParcelImportSection but
 *   adapted for the admin slide-in context (compact, inline save, different
 *   state shape and props — not a shared component).
 * - No mock data, no hardcoded URLs.
 *
 * @component
 * Purpose:     Inline parcel data configuration form for departments without
 *              existing configuration.
 * Props:       tenant, onSave (callback), onTriggerImport (callback),
 *              saving, triggering.
 * Behavior:    Auto-matches cache file by jurisdiction; lets admin pick
 *              filter mode, hazard levels, and tokens; saves via hook.
 * Data Source: local (Dexie via useParcelCacheFiles hook).
 * Constraints: no direct API calls; all mutations via parent hook callbacks.
 * Performance: one liveQuery subscription (useParcelCacheFiles).
 */

import { useState, useMemo, useCallback } from 'react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import {
  FileText, MapPin, Loader2, Save, Zap, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, Database, Users,
} from 'lucide-react';
import { HAZARD_LEVEL_LABELS, type FireHazardLevel } from '@firesquirrel/core-domain';
import { useParcelCacheFiles } from '../hooks/useParcelCacheFiles';
import type { GeoCacheFile } from '../services/parcelDataAdminService';

type FilterMode = 'all' | 'department' | 'fireHazard';

interface Props {
  tenant: any;
  onSave: (config: {
    path: 'regrid';
    regridFileId: string;
    regridFileName: string;
    filterMode: FilterMode;
    hazardLevels: string[];
    createOwnerContacts: boolean;
    tokensPerYear: number;
  }) => Promise<boolean>;
  onTriggerImport: (overrides?: {
    filterMode?: FilterMode;
    hazardLevels?: string[];
    createOwnerContacts?: boolean;
  }) => Promise<string | null>;
  saving: boolean;
  triggering: boolean;
}

/**
 * @component ParcelDataConfigForm
 *
 * Purpose:
 * - Render an inline configuration form for departments without Regrid
 *   parcel data configuration. Auto-matches the cache file from the
 *   department's jurisdiction, lets the admin choose filter mode / hazard
 *   levels / tokens, saves the config, and optionally triggers an import.
 *
 * Props:
 * - tenant: The current department record (must contain `_id`; optionally
 *   `jurisdiction` with county/state/fipsCode, `boundary` for spatial filter).
 * - onSave: Callback to save the config via the hook.
 * - onTriggerImport: Callback to trigger an admin-bypass import.
 * - saving: True while the save is in flight.
 * - triggering: True while the import trigger is in flight.
 *
 * Behavior:
 * - Auto-matches a cache file from the department's jurisdiction on mount.
 * - Lets the admin browse all cache files to override the auto-match.
 * - Filter mode selection is consistent with the onboarding wizard.
 * - Save button calls onSave; after save, a "Trigger Import" button appears.
 *
 * Data Source:
 * - local (Dexie via useParcelCacheFiles hook).
 *
 * Constraints:
 * - No direct API calls; all mutations via parent hook callbacks.
 */
export default function ParcelDataConfigForm({
  tenant,
  onSave,
  onTriggerImport,
  saving,
  triggering,
}: Props) {
  const { theme } = useTheme();
  const { files, loading: filesLoading, findAutoMatchedFile } = useParcelCacheFiles();

  // Resolve jurisdiction info for auto-match
  const jurisdiction = tenant?.jurisdiction ?? {};
  const countyCode = typeof jurisdiction?.county?.code === 'string' ? jurisdiction.county.code : undefined;
  const fipsCode = typeof jurisdiction?.fipsCode === 'string' ? jurisdiction.fipsCode : undefined;
  const stateCode = typeof jurisdiction?.state?.code === 'string' ? jurisdiction.state.code : undefined;
  const stateName = typeof jurisdiction?.state?.name === 'string' ? jurisdiction.state.name : undefined;
  const countyName = typeof jurisdiction?.county?.name === 'string' ? jurisdiction.county.name : undefined;
  const hasBoundary = !!tenant?.boundary && Object.keys(tenant.boundary).length > 0;

  // Auto-match the cache file
  const autoMatch = useMemo(
    () => findAutoMatchedFile(fipsCode ?? countyCode, stateCode, countyName, stateName),
    [findAutoMatchedFile, fipsCode, countyCode, stateCode, countyName, stateName],
  );

  // Pre-populate from existing tenant.parcelImport when reconfiguring so the
  // admin sees their current saved values instead of hardcoded defaults.
  const existingConfig = tenant?.parcelImport;
  const existingTokens = tenant?.updateTokens;

  // Form state — initialized from existing config (reconfigure) or defaults
  const [selectedFileId, setSelectedFileId] = useState<string | null>(
    existingConfig?.regridFileId ?? autoMatch?._id ?? null,
  );
  const [filterMode, setFilterMode] = useState<FilterMode>(
    (existingConfig?.filterMode as FilterMode) ?? 'all',
  );
  const [hazardLevels, setHazardLevels] = useState<string[]>(
    Array.isArray(existingConfig?.hazardLevels) && existingConfig.hazardLevels.length > 0
      ? existingConfig.hazardLevels
      : ['extreme'],
  );
  // Contact creation is opt-in: a parcel import must never silently create
  // contact records unless the department explicitly asked for it.
  const [createOwnerContacts, setCreateOwnerContacts] = useState<boolean>(
    existingConfig?.createOwnerContacts === true,
  );
  const [tokensPerYear, setTokensPerYear] = useState<number>(
    typeof existingTokens?.tokensPerYear === 'number' ? existingTokens.tokensPerYear : 1,
  );
  const [showBrowseAll, setShowBrowseAll] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update selected file when auto-match resolves
  const effectiveFileId = selectedFileId ?? autoMatch?._id ?? null;
  const selectedFile = files.find((f) => f._id === effectiveFileId) ?? autoMatch;

  const handleSelectFile = useCallback((file: GeoCacheFile) => {
    setSelectedFileId(file._id);
    setShowBrowseAll(false);
  }, []);

  const handleHazardToggle = useCallback((level: FireHazardLevel) => {
    setHazardLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedFile) {
      setError('Select a Regrid cache file first.');
      return;
    }
    // Validate filter mode requirements
    if ((filterMode === 'department' || filterMode === 'fireHazard') && !hasBoundary) {
      setError('Department boundary is required for this filter mode but is not set on the department.');
      return;
    }
    if (filterMode === 'fireHazard' && hazardLevels.length === 0) {
      setError('Select at least one hazard level for fire hazard overlay mode.');
      return;
    }
    setError(null);
    const ok = await onSave({
      path: 'regrid',
      regridFileId: selectedFile._id,
      regridFileName: selectedFile.filename,
      filterMode,
      hazardLevels,
      createOwnerContacts,
      tokensPerYear,
    });
    if (ok) {
      setSaved(true);
    } else {
      setError('Failed to save configuration. Check the error banner above.');
    }
  }, [selectedFile, filterMode, hazardLevels, createOwnerContacts, tokensPerYear, hasBoundary, onSave]);

  const handleTriggerImport = useCallback(async () => {
    setError(null);
    const jobId = await onTriggerImport({ filterMode, hazardLevels, createOwnerContacts });
    if (!jobId) {
      setError('Failed to trigger import. Check the error banner above.');
    }
  }, [onTriggerImport, filterMode, hazardLevels, createOwnerContacts]);

  const filterModeOptions: Array<{ value: FilterMode; label: string; hint: string }> = [
    { value: 'all', label: 'All parcels', hint: 'Import every parcel in the county' },
    { value: 'department', label: 'Department boundary', hint: 'Only parcels inside the department boundary' },
    { value: 'fireHazard', label: 'Fire hazard overlay', hint: 'Only parcels in selected hazard zones' },
  ];

  return (
    <div className="space-y-4">
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {/* Saved confirmation */}
      {saved && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 text-xs rounded">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Configuration saved. You can now trigger an import below.
        </div>
      )}

      {/* Auto-match status */}
      <div className="p-3 rounded-lg" style={{ background: theme.expandedBg }}>
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin className="h-3.5 w-3.5" style={{ color: theme.textSecondary }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Matched Cache File
          </h4>
        </div>
        {filesLoading ? (
          <div className="flex items-center gap-2 text-xs" style={{ color: theme.textSecondary }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading cache files…
          </div>
        ) : selectedFile ? (
          <div>
            <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
              {selectedFile.county}, {selectedFile.stateName ?? selectedFile.state}
            </div>
            <div className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
              FIPS <code className="font-mono">{selectedFile.fipsCode}</code> —{' '}
              {selectedFile.featureCount?.toLocaleString() ?? '—'} parcels —{' '}
              {selectedFile.status === 'completed' ? (
                <span className="text-green-600">Ready</span>
              ) : (
                <span className="text-amber-600">{selectedFile.status ?? 'Unknown'}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowBrowseAll((v) => !v)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
            >
              {showBrowseAll ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showBrowseAll ? 'Hide other files' : 'Browse all cache files'}
            </button>
          </div>
        ) : (
          <div>
            <p className="text-sm mb-1" style={{ color: theme.textPrimary }}>
              No cache file matched for this department's jurisdiction.
            </p>
            <p className="text-xs mb-2" style={{ color: theme.textSecondary }}>
              {fipsCode || countyCode
                ? `FIPS ${fipsCode ?? countyCode} — the cache file for this county has not been uploaded.`
                : 'No county FIPS or state+county set on the department jurisdiction.'}
              {' '}Upload it via Settings → Parcel Data → Cache Files.
            </p>
            <button
              type="button"
              onClick={() => setShowBrowseAll((v) => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
            >
              {showBrowseAll ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showBrowseAll ? 'Hide' : 'Browse all cache files'}
            </button>
          </div>
        )}

        {/* Browse all cache files */}
        {showBrowseAll && (
          <div className="mt-3 max-h-64 overflow-y-auto border rounded-lg divide-y" style={{ borderColor: theme.cardBorder }}>
            {files.length === 0 && !filesLoading && (
              <div className="p-3 text-xs" style={{ color: theme.textSecondary }}>
                No cache files available. Upload via Settings → Parcel Data.
              </div>
            )}
            {files.map((f) => (
              <label
                key={f._id}
                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="radio"
                  name="cacheFile"
                  checked={effectiveFileId === f._id}
                  onChange={() => handleSelectFile(f)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1 text-xs">
                  <div className="font-medium" style={{ color: theme.textPrimary }}>
                    {f.county}, {f.stateName ?? f.state}
                  </div>
                  <div style={{ color: theme.textSecondary }}>
                    FIPS {f.fipsCode} — {f.featureCount?.toLocaleString() ?? '—'} parcels —{' '}
                    {f.status === 'completed' ? 'Ready' : f.status ?? 'Unknown'}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Spatial filter mode */}
      <div className="p-3 rounded-lg" style={{ background: theme.expandedBg }}>
        <div className="flex items-center gap-1.5 mb-2">
          <Database className="h-3.5 w-3.5" style={{ color: theme.textSecondary }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Spatial Filtering Mode
          </h4>
        </div>
        <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
          Choose which parcels to import from the matched county file.
        </p>
        <div className="grid grid-cols-1 gap-2">
          {filterModeOptions.map((opt) => {
            const disabled = (opt.value === 'department' || opt.value === 'fireHazard') && !hasBoundary;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => !disabled && setFilterMode(opt.value)}
                disabled={disabled}
                className={`border-2 rounded-lg px-3 py-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  filterMode === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="text-xs font-semibold text-gray-900">{opt.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {opt.hint}
                  {disabled && ' (no department boundary set)'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Hazard levels */}
        {filterMode === 'fireHazard' && (
          <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold text-gray-700 mb-2">Hazard levels to include</div>
            <div className="flex flex-wrap gap-4">
              {(['extreme', 'high', 'medium', 'low'] as const).map((level) => {
                const selected = hazardLevels.includes(level);
                return (
                  <label key={level} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleHazardToggle(level)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {HAZARD_LEVEL_LABELS[level]}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Owner contact creation */}
      <div className="p-3 rounded-lg" style={{ background: theme.expandedBg }}>
        <div className="flex items-center gap-1.5 mb-2">
          <Users className="h-3.5 w-3.5" style={{ color: theme.textSecondary }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Owner Contacts
          </h4>
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={createOwnerContacts}
            onChange={(e) => setCreateOwnerContacts(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-xs" style={{ color: theme.textPrimary }}>
            Create a primary-owner contact for each imported parcel
            <span className="block text-[11px] mt-0.5" style={{ color: theme.textSecondary }}>
              Off by default. When enabled, the import creates or updates one
              contact per parcel from the Regrid owner and mailing address, and
              links it to the parcel as the responsible party. Ownership facts
              stay on the parcel either way.
            </span>
          </span>
        </label>
      </div>

      {/* Update tokens */}
      <div className="p-3 rounded-lg" style={{ background: theme.expandedBg }}>
        <div className="flex items-center gap-1.5 mb-2">
          <FileText className="h-3.5 w-3.5" style={{ color: theme.textSecondary }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Update Tokens
          </h4>
        </div>
        <p className="text-xs mb-2" style={{ color: theme.textSecondary }}>
          Number of parcel data updates per year (1 included; additional tokens
          can be purchased at renewal).
        </p>
        <input
          type="number"
          min={1}
          max={12}
          value={tokensPerYear}
          onChange={(e) => setTokensPerYear(Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1)))}
          className="w-20 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
          aria-label="Tokens per year"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !selectedFile}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {saved && selectedFile?.status === 'completed' && (
          <button
            type="button"
            onClick={handleTriggerImport}
            disabled={triggering}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
            title="Trigger a Regrid parcel import without consuming a token (admin bypass)"
          >
            {triggering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {triggering ? 'Triggering…' : 'Trigger Import (No Token)'}
          </button>
        )}
        {saved && selectedFile && selectedFile.status !== 'completed' && (
          <span className="text-xs text-amber-600 inline-flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            Cache file is not ready (status: {selectedFile.status ?? 'unknown'})
          </span>
        )}
      </div>
    </div>
  );
}
